import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Download, BookOpen, Compass, ListChecks, ShieldCheck, HelpCircle, Sparkles, X, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import CTABanner from "../components/CTABanner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SUB_BRANDS = [
  { code: "ITSM360", lens: "IT service management — incident, problem, change, request, knowledge, CSI." },
  { code: "AI360", lens: "Generative-AI readiness — strategy, governance, model lifecycle, ethics." },
  { code: "DATA360", lens: "Data management — DAMA-DMBOK anchored, quality, governance, architecture." },
  { code: "PMO360", lens: "Project / portfolio governance — MoP/MSP, demand, value, assurance." },
  { code: "Change360", lens: "Change management — ADKAR, communication, sustainment." },
  { code: "Digital360", lens: "Digital strategy & delivery — product, platform, customer experience." },
  { code: "Operating360", lens: "Operating-model fitness — design, decision rights, accountability." },
  { code: "Supplier360", lens: "Supplier governance — selection, contract, performance, exit." },
  { code: "Benefits360", lens: "Benefits realisation — baselines, tracking, attribution." },
  { code: "Govern360", lens: "Enterprise governance — risk, controls, audit, compliance." },
  { code: "Capability360", lens: "Workforce capability — skills, learning, succession." },
  { code: "Stakeholder360", lens: "Stakeholder engagement — mapping, segmentation, value-delivery." },
];

const GLOSSARY = [
  ["Indicator", "A single observable practice scored 1–5. Several indicators roll up to a dimension."],
  ["Dimension", "A grouping of related indicators. Each dimension has a weight in the framework."],
  ["Weighted score", "Dimension average × weight, summed to the overall — so the framework's most important dimensions move the headline."],
  ["Dealbreaker cap", "When a structurally critical dimension falls below a floor, the overall score is capped regardless of other strengths."],
  ["Completeness %", "Proportion of indicators actually answered. Partial assessments are flagged rather than masked."],
  ["Lite assessment", "Public, anonymous, 8–12 indicator snapshot. No contact details required."],
  ["Full engagement", "Identified, contracted assessment with evidence capture, Opus enrichment and branded exports."],
  ["CSI move", "A recommended Continuous-Service-Improvement action drawn from the framework's catalogue."],
  ["Defensibility", "Evidence-weight reconciliation — how well the captured narratives support the self-reported scores."],
  ["Cost confidence", "High / medium / low — drops when the plan uses unknown roles that fell back to the rate card's default rate."],
];

const FAQ = [
  {
    q: "What's the difference between the lite assessment and a full engagement?",
    a: "The lite assessment is a public, anonymous, ~8–12 question snapshot per sub-brand. You can run all 12 in your browser and see a personalised portfolio radar — we never see your name unless you choose to share it. A full engagement is a contracted, named, evidence-backed assessment that produces branded board packs (PDF/PPTX/DOCX/XLSX), an Opus-augmented executive project plan and a defensible price.",
  },
  {
    q: "Do my answers and scores affect each other across sub-brands?",
    a: "No. Each of the 12 sub-brands has its own rubric, weights and dealbreakers. They produce independent scores. The portfolio radar shows your scores side-by-side; it does not average or cross-influence them.",
  },
  {
    q: "Can I trust the scoring to be consistent?",
    a: "Yes — the scoring kernel is strictly deterministic. The same answers always produce the same score. The Claude Opus 4.8 enrichment layer can never change a score; it only generates the narrative, the project plan, evidence reconciliation and tailored CSI move language on top of the deterministic numbers.",
  },
  {
    q: "How is the engagement budget calculated?",
    a: "A deterministic rate card. Opus proposes the resource model (FTE-weeks per role per move). The pricing engine multiplies that by stored day-rates, utilisation, region/sector/size modifiers and produces a line-by-line audit you can defend in front of procurement.",
  },
  {
    q: "What's the EKSELENS360 Maturity Engine made of?",
    a: "12 deterministic frameworks, an evidence-aware Claude Opus 4.8 enrichment layer, and a deterministic pricing engine. Output: PDF Board Pack (~20 pages), PPTX Workshop Deck, XLSX Analyst Workbook, DOCX Improvement Plan.",
  },
  {
    q: "Where is my data stored? Can I delete it?",
    a: "Anonymous lite-assessment data is keyed only to a browser-generated device_id. You can wipe it at any time using the self-service tool on the /privacy page (GDPR Article 17). Identified engagements follow the published retention policy and erasure path.",
  },
  {
    q: "How do we access the documents for our engagement?",
    a: "Every named engagement gets a secure client portal. You sign in with your work email via a one-time magic link (no password). Under the Documents tab you'll see only the deliverables published to your engagement — never another client's. New documents show a 'New' badge until you open them, and you're emailed whenever we publish one.",
  },
  {
    q: "Can you help with IT-delivery and DevOps operating-model / org design?",
    a: "Yes. As part of an engagement we run a Delivery Target-Operating-Model (TOM) analysis: from your context (scale, regulation, delivery maturity, cloud/platform footprint) we recommend the right operating-model archetype — foundational, scaling-product, federated-enterprise or regulated-platform — with a prioritised org-design, automation, DevOps and AI backlog. It's a deterministic, defensible method (not a generic AI opinion), and every recommendation can be tracked through to delivery in the engagement's RAID register.",
  },
];

const PILLAR_CARDS = [
  {
    icon: Compass,
    title: "1. Pick a starting point",
    body: "Browse the 12 sub-brands below, or use the public Resources page to run a lite self-assessment for any of them. Each lite takes 5–10 minutes.",
  },
  {
    icon: ListChecks,
    title: "2. Build a portfolio view",
    body: "As you complete more lites, the radar on /services personalises to show your real cross-portfolio polygon. Lowest sub-brand and next-best suggestion update automatically.",
  },
  {
    icon: Sparkles,
    title: "3. Convert to an engagement",
    body: "Click Send my portfolio to Ekselens to share what you've completed with us. We follow up with a board-grade plan, pricing and the option to run the full engagement.",
  },
];

const MethodologyRequestModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ full_name: "", email: "", organisation: "", role: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;
  const valid = form.full_name.trim().length >= 2 && form.email.includes("@") && form.organisation.trim().length >= 2 && form.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/public/methodology/request`, form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the document. Try again or use the direct download.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-[2px] flex items-center justify-center px-4" data-testid="methodology-modal">
      <div className="bg-white max-w-md w-full p-7 border border-zinc-300 shadow-2xl relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-700" aria-label="Close" data-testid="methodology-modal-close">
          <X size={18} />
        </button>
        {done ? (
          <div className="text-center py-3" data-testid="methodology-modal-success">
            <CheckCircle2 size={32} className="text-emerald-600 mx-auto" />
            <h3 className="ek-heading text-xl text-black mt-4">Request received.</h3>
            <p className="text-[14.5px] text-zinc-700 mt-3 font-light leading-relaxed">
              Thanks — we&rsquo;ve logged your request and sent a confirmation to <strong>{form.email}</strong>. A member of our team will review it and be in touch shortly.
            </p>
            <button type="button" onClick={onClose} className="ek-btn ek-btn-primary mt-6 text-[13px] px-5 py-2.5" data-testid="methodology-modal-success-close">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-[#7737F6]" />
              <div className="ek-eyebrow text-[#7737F6] text-[11px]">Methodology Paper</div>
            </div>
            <h3 className="ek-heading text-xl text-black mt-2">Request access</h3>
            <p className="text-[13.5px] text-zinc-600 mt-2 font-light">The methodology paper is a confidential document. Register your interest and we&rsquo;ll review your request and be in touch — we&rsquo;ll also answer any questions you have.</p>
            <form onSubmit={submit} className="mt-5 space-y-3" data-testid="methodology-modal-form">
              <input type="text" placeholder="Full name *" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-[16px] focus:outline-none focus:border-[#7737F6]" data-testid="methodology-modal-name" required />
              <input type="email" placeholder="Work email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-[16px] focus:outline-none focus:border-[#7737F6]" data-testid="methodology-modal-email" required />
              <input type="text" placeholder="Organisation *" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-[16px] focus:outline-none focus:border-[#7737F6]" data-testid="methodology-modal-org" required />
              <input type="text" placeholder="Role (optional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-[16px] focus:outline-none focus:border-[#7737F6]" data-testid="methodology-modal-role" />
              <label className="flex items-start gap-2.5 text-[12.5px] text-zinc-700 font-light leading-relaxed">
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" data-testid="methodology-modal-consent" />
                <span>I consent to Ekselens Consulting contacting me about this request. See our <Link to="/privacy" className="text-[#7737F6] hover:underline">privacy policy</Link>.</span>
              </label>
              <button type="submit" disabled={!valid || submitting} className="ek-btn ek-btn-primary w-full inline-flex items-center justify-center gap-2 text-[13px] px-5 py-3 disabled:opacity-50" data-testid="methodology-modal-submit">
                {submitting ? "Sending…" : (<><Mail size={14} /> Request access</>)}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const Help = () => {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">Help &amp; methodology</div>
          <h1
            className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4 max-w-4xl leading-tight"
            data-testid="help-headline"
          >
            How EKSELENS360 works
          </h1>
          <p className="text-zinc-200 font-light text-lg md:text-xl mt-6 max-w-3xl">
            A user guide for the EKSELENS360 Maturity Engine — what the 12 sub-brands measure, how the scoring works, what you get out, and where your data goes.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="ek-btn ek-btn-primary inline-flex items-center gap-2"
              data-testid="help-download-methodology-pdf"
            >
              <Mail size={16} />
              Request the Methodology Paper
            </button>
            <Link
              to="/resources"
              className="text-[#A179FA] hover:text-white text-sm font-medium tracking-[0.08em] uppercase"
              data-testid="help-resources-link"
            >
              Run a lite assessment →
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="ek-container max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {PILLAR_CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="border border-zinc-200 p-7 hover:border-[#7737F6] transition-colors"
                  data-testid={`help-pillar-card-${card.title.split(".")[0].trim()}`}
                >
                  <Icon size={22} className="text-[#7737F6]" />
                  <h3 className="ek-heading text-lg md:text-xl text-black mt-5">{card.title}</h3>
                  <p className="ek-body text-[15px] text-zinc-700 mt-3 font-light leading-relaxed">{card.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F2F2F3] py-16 md:py-24">
        <div className="ek-container max-w-6xl">
          <div className="flex items-center gap-3">
            <BookOpen size={20} className="text-[#7737F6]" />
            <div className="ek-eyebrow text-[#7737F6]">The 12 sub-brands</div>
          </div>
          <h2 className="ek-heading text-3xl md:text-4xl text-black mt-3">What each one measures</h2>
          <p className="ek-body text-zinc-700 font-light mt-4 max-w-3xl">
            Each sub-brand is its own framework with 5–8 dimensions and 4–10 indicators per dimension, anchored to public reference frameworks (ITIL 4, NIST AI RMF, DAMA-DMBOK, MoP/MSP, ADKAR, Treasury Green Book, etc.) for category alignment only.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
            {SUB_BRANDS.map((s) => (
              <div key={s.code} className="border-l-2 border-[#7737F6] pl-5" data-testid={`help-sub-brand-${s.code.toLowerCase()}`}>
                <div className="ek-eyebrow text-[#7737F6] text-[11px]">{s.code}</div>
                <p className="ek-body text-[15px] text-zinc-800 font-light mt-2 leading-relaxed">{s.lens}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE360 — the reinvention product */}
      <section className="bg-white py-16 md:py-24" data-testid="help-enterprise360">
        <div className="ek-container max-w-6xl">
          <div className="flex items-center gap-3">
            <Sparkles size={20} className="text-[#7737F6]" />
            <div className="ek-eyebrow text-[#7737F6]">Enterprise360</div>
          </div>
          <h2 className="ek-heading text-3xl md:text-4xl text-black mt-3">
            Beyond a single function — whole-enterprise reinvention
          </h2>
          <p className="ek-body text-zinc-700 font-light mt-4 max-w-3xl">
            The twelve sub-brands assess a function. <strong>Enterprise360</strong> is the other half of
            the 360 family: a reinvention engine that resolves your business archetype and assembles a
            tailored, board-grade transformation blueprint across twelve end-to-end value streams — from
            strategy to execution — on the same maturity scale and system of proof.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-zinc-200 p-6" data-testid="help-e360-card-blueprint">
              <Compass size={18} className="text-[#7737F6]" />
              <h3 className="ek-heading text-lg text-black mt-4">The Reinvention Blueprint</h3>
              <p className="ek-body text-[15px] text-zinc-700 font-light mt-2 leading-relaxed">
                We resolve your archetype (sector, operating and customer model) and assemble the right
                "recipe" of value streams, target operating model and phased roadmap — not a generic template.
              </p>
            </div>
            <div className="border border-zinc-200 p-6" data-testid="help-e360-card-assurance">
              <ListChecks size={18} className="text-[#7737F6]" />
              <h3 className="ek-heading text-lg text-black mt-4">Delivery &amp; Assurance Hub</h3>
              <p className="ek-body text-[15px] text-zinc-700 font-light mt-2 leading-relaxed">
                A live accountability layer: single-source-of-truth data, pre-export QA gates and a
                defensible Model Risk Management framework, so every recommendation traces back to evidence.
              </p>
            </div>
            <div className="border border-zinc-200 p-6" data-testid="help-e360-card-regulatory">
              <ShieldCheck size={18} className="text-[#7737F6]" />
              <h3 className="ek-heading text-lg text-black mt-4">Regulatory coverage</h3>
              <p className="ek-body text-[15px] text-zinc-700 font-light mt-2 leading-relaxed">
                Each blueprint is mapped against the regimes that matter to your sector (DORA, EU AI Act,
                UK Operational Resilience, ISO 22301 and more) with a coverage matrix and portfolio heatmap.
              </p>
            </div>
            <div className="border border-zinc-200 p-6" data-testid="help-e360-card-portal">
              <BookOpen size={18} className="text-[#7737F6]" />
              <h3 className="ek-heading text-lg text-black mt-4">Your secure client portal</h3>
              <p className="ek-body text-[15px] text-zinc-700 font-light mt-2 leading-relaxed">
                Engagements run through a secure portal where you review your blueprint, evidence and
                progress. You'll receive a one-time access code by email — see the client self-assessment to begin.
              </p>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/enterprise360" className="ek-btn ek-btn-primary text-[13px] px-5 py-2.5" data-testid="help-e360-learn-more">
              Explore Enterprise360 <Compass size={15} />
            </Link>
            <Link to="/how-it-works" className="ek-btn ek-btn-outline-dark text-[13px] px-5 py-2.5" data-testid="help-e360-how-it-works">
              See how it works end to end
            </Link>
          </div>
        </div>
      </section>


      <section className="bg-white py-16 md:py-24">
        <div className="ek-container max-w-4xl">
          <div className="flex items-center gap-3">
            <ShieldCheck size={20} className="text-[#7737F6]" />
            <div className="ek-eyebrow text-[#7737F6]">How scoring works</div>
          </div>
          <h2 className="ek-heading text-3xl md:text-4xl text-black mt-3">Deterministic by design</h2>
          <div className="ek-body text-zinc-800 space-y-5 mt-6 text-[16px] leading-relaxed font-light">
            <p>
              Every indicator collects one of 5 numeric responses with a written label. The engine then computes the dimension averages, applies per-dimension weights, sums them to the overall, and applies a dealbreaker cap when a structurally critical dimension falls below its floor.
            </p>
            <p>
              The LLM <strong className="font-medium text-black">cannot</strong> change a score — it is constrained to post-scoring enrichments (executive narrative, project plan, evidence reconciliation, CSI move tailoring). This is what makes the engine defensible in front of a steering committee.
            </p>
            <div className="bg-[#FAF7FF] border-l-2 border-[#7737F6] p-5 md:p-6 mt-4">
              <div className="ek-eyebrow text-[#7737F6] text-[11px]">Why deterministic</div>
              <p className="mt-2 text-zinc-800 text-[15px]">A maturity score is only useful if it can be re-run by anyone with the same answers and produce the same result. That property is non-negotiable for procurement.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F2F2F3] py-16 md:py-24">
        <div className="ek-container max-w-5xl">
          <div className="ek-eyebrow text-[#7737F6]">Glossary</div>
          <h2 className="ek-heading text-3xl md:text-4xl text-black mt-3">Key terms</h2>
          <div className="mt-10 divide-y divide-zinc-300/70 border-t border-zinc-300/70">
            {GLOSSARY.map(([term, def]) => (
              <div key={term} className="py-5 grid grid-cols-1 md:grid-cols-[180px,1fr] gap-3 md:gap-8" data-testid={`help-glossary-${term.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="ek-heading text-[15px] text-black">{term}</div>
                <p className="ek-body text-[15px] text-zinc-700 font-light leading-relaxed">{def}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="ek-container max-w-4xl">
          <div className="flex items-center gap-3">
            <HelpCircle size={20} className="text-[#7737F6]" />
            <div className="ek-eyebrow text-[#7737F6]">FAQ</div>
          </div>
          <h2 className="ek-heading text-3xl md:text-4xl text-black mt-3">Frequently asked questions</h2>
          <div className="mt-10 space-y-8">
            {FAQ.map((f, i) => (
              <div key={f.q} data-testid={`help-faq-${i}`}>
                <h3 className="ek-heading text-lg md:text-xl text-black">{f.q}</h3>
                <p className="ek-body text-[15.5px] text-zinc-700 mt-3 font-light leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 p-7 md:p-9 border border-zinc-300 bg-[#FAFAFB] flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="ek-eyebrow text-[#7737F6] text-[11px]">Need more depth</div>
              <h3 className="ek-heading text-xl md:text-2xl text-black mt-2">The full Methodology Paper</h3>
              <p className="ek-body text-[14.5px] text-zinc-700 font-light mt-2 max-w-xl">
                A defensible single-document explanation of the scoring, the enrichment layer, the pricing engine and the artefacts — for procurement, sponsoring executives and engagement leads.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="ek-btn ek-btn-primary inline-flex items-center gap-2 shrink-0"
              data-testid="help-download-methodology-pdf-bottom"
            >
              <Mail size={16} />
              Request access
            </button>
          </div>
        </div>
      </section>

      <CTABanner
        eyebrow="Run a diagnostic"
        title="Pick a sub-brand and try a lite assessment."
        body="Anonymous, free, 5–10 minutes per sub-brand. Build up your portfolio radar — then book a discovery conversation to talk it through."
      />
      <MethodologyRequestModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default Help;
